import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { ModoPeriodo, Periodo } from '@adelante/shared';

const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

/** Etiqueta única para los meses (la usan también las pantallas para títulos). */
export const NOMBRES_MESES = MESES;

const ANIOS = [2024, 2025, 2026];
const TRIMESTRES: Array<{ value: 1 | 2 | 3 | 4; label: string }> = [
  { value: 1, label: 'Q1 (Ene–Mar)' },
  { value: 2, label: 'Q2 (Abr–Jun)' },
  { value: 3, label: 'Q3 (Jul–Sep)' },
  { value: 4, label: 'Q4 (Oct–Dic)' },
];

const TABS: Array<{ value: ModoPeriodo; label: string }> = [
  { value: 'mes', label: 'Mes' },
  { value: 'trimestre', label: 'Trimestre' },
  { value: 'anio', label: 'Año' },
  { value: 'rango', label: 'Rango' },
];

interface Props {
  periodo: Periodo;
  onSetModo: (modo: ModoPeriodo) => void;
  onSetAnio: (anio: number) => void;
  onSetMes: (mes: number) => void;
  onSetTrimestre: (q: 1 | 2 | 3 | 4) => void;
  onSetDesde: (anio: number, mes: number) => void;
  onSetHasta: (anio: number, mes: number) => void;
  /** Si true, el componente se renderiza en columna (footprint chico). */
  compacto?: boolean;
}

export function SelectorPeriodo({
  periodo,
  onSetModo,
  onSetAnio,
  onSetMes,
  onSetTrimestre,
  onSetDesde,
  onSetHasta,
}: Props) {
  return (
    <div className="flex flex-col gap-2">
      {/* Tabs de modo */}
      <div className="inline-flex rounded-md border bg-muted/30 p-0.5 text-xs">
        {TABS.map((tab) => {
          const activo = periodo.modo === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => onSetModo(tab.value)}
              className={cn(
                'rounded px-3 py-1 transition-colors',
                activo
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Controles dependientes del modo */}
      <div className="flex flex-wrap items-center gap-2">
        {periodo.modo === 'mes' && (
          <>
            <SelectMes value={periodo.mes} onChange={onSetMes} />
            <SelectAnio value={periodo.anio} onChange={onSetAnio} />
          </>
        )}

        {periodo.modo === 'trimestre' && (
          <>
            <Select
              value={String(periodo.trimestre ?? 1)}
              onValueChange={(v) => onSetTrimestre(Number(v) as 1 | 2 | 3 | 4)}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRIMESTRES.map((q) => (
                  <SelectItem key={q.value} value={String(q.value)}>
                    {q.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <SelectAnio value={periodo.anio} onChange={onSetAnio} />
          </>
        )}

        {periodo.modo === 'anio' && <SelectAnio value={periodo.anio} onChange={onSetAnio} />}

        {periodo.modo === 'rango' && (
          <>
            <span className="text-xs text-muted-foreground">Desde</span>
            <SelectMes
              value={periodo.desdeMes ?? periodo.mes}
              onChange={(m) => onSetDesde(periodo.desdeAnio ?? periodo.anio, m)}
            />
            <SelectAnio
              value={periodo.desdeAnio ?? periodo.anio}
              onChange={(a) => onSetDesde(a, periodo.desdeMes ?? periodo.mes)}
            />
            <span className="text-xs text-muted-foreground">Hasta</span>
            <SelectMes
              value={periodo.hastaMes ?? periodo.mes}
              onChange={(m) => onSetHasta(periodo.hastaAnio ?? periodo.anio, m)}
            />
            <SelectAnio
              value={periodo.hastaAnio ?? periodo.anio}
              onChange={(a) => onSetHasta(a, periodo.hastaMes ?? periodo.mes)}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

function SelectMes({ value, onChange }: { value: number; onChange: (m: number) => void }) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="w-[130px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {MESES.map((nombre, i) => (
          <SelectItem key={nombre} value={String(i + 1)}>
            {nombre}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SelectAnio({ value, onChange }: { value: number; onChange: (a: number) => void }) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="w-[90px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ANIOS.map((a) => (
          <SelectItem key={a} value={String(a)}>
            {a}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
